import React from 'react'
import { ColDef, ColGroupDef, GridOptions, ICellRendererParams } from 'ag-grid-community'
import { Locale, Translation } from 'locale'
import { ClearingHouseInteraction, DateFormat } from 'api'
import {
  AgGridWrapper,
  DefaultCellContentWrapper,
  gridOptionsBase,
  TypedCellClassParams,
  TypedValueGetterParams
} from 'view/components/datagrid'
import { ClearingMessageTypeHelper } from 'model'
import { formatTimestamp } from 'locale/Locale'
import { ClearingHouseInteractionViewButton } from './ClearingHouseInteractionViewButton'
import './clearingHouseInteractionGrid.css'
import { isNil } from 'lodash-es'

export interface ClearingHouseInteractionGridProps {
  interactions: ClearingHouseInteraction[]
}

const ClearingHouseInteractionInfoButtonCellRenderer = React.forwardRef((props: ICellRendererParams, ref: React.Ref<any>) => {
  const clearingHouseInteraction = props.data

  return (
    <DefaultCellContentWrapper>
      <ClearingHouseInteractionViewButton ref={ref} clearingHouseInteraction={clearingHouseInteraction} />
    </DefaultCellContentWrapper>
  )
})

const noInteractionsPlaceholder = () => <div>{Locale.translate(Translation.SC_NO_CLEARING_INTERACTIONS_AVAILABLE)}</div>

const applyErrorClass = (params: TypedCellClassParams<ClearingHouseInteraction>) =>
  !isNil(params.data) && !isNil(params.data.error) && isNil(params.data.errorResolutionEvent)

const applyManualResolutionClass = (params: TypedCellClassParams<ClearingHouseInteraction>) =>
  !isNil(params.data) && !isNil(params.data.errorResolutionEvent)

const cellClass = 'clearing-grid'
const cellClassRules = { error: applyErrorClass, manualResolution: applyManualResolutionClass }

const columnDefs: (ColDef | ColGroupDef)[] = [
  {
    colId: 'TYPE',
    valueGetter: (params: TypedValueGetterParams<ClearingHouseInteraction>) =>
      params.data && ClearingMessageTypeHelper.getName(params.data.messageType),
    cellClass,
    cellClassRules,
    headerName: Locale.translate(Translation.TC_CLEARING_MESSAGE_TYPE),
    suppressMovable: true,
    resizable: false,
    sortable: false,
    width: 70
  },
  {
    colId: 'SENT',
    valueGetter: (params: TypedValueGetterParams<ClearingHouseInteraction>) =>
      params.data && formatTimestamp(params.data.messageSentInstant, DateFormat.EU),
    cellClass,
    cellClassRules,
    headerName: Locale.translate(Translation.TC_CLEARING_REQUEST_SENT_AT),
    suppressMovable: true,
    resizable: false,
    sortable: false,
    width: 150
  },
  {
    colId: 'RECEIVED',
    valueGetter: (params: TypedValueGetterParams<ClearingHouseInteraction>) =>
      params.data && formatTimestamp(params.data.responseReceivedInstant, DateFormat.EU),
    cellClass,
    cellClassRules,
    headerName: Locale.translate(Translation.TC_CLEARING_RESPONSE_RECEIVED_AT),
    suppressMovable: true,
    resizable: false,
    sortable: false,
    width: 150
  },
  {
    colId: 'VIEW',
    cellRenderer: ClearingHouseInteractionInfoButtonCellRenderer,
    equals: () => false,
    headerName: '',
    suppressMovable: true,
    resizable: false,
    sortable: false,
    width: 80
  }
]

const gridOptions: GridOptions = {
  ...gridOptionsBase,
  groupDefaultExpanded: 0,
  getRowId: ({ data }) => data.id
}

export const ClearingHouseInteractionGrid: React.FunctionComponent<ClearingHouseInteractionGridProps> = props => {
  const { interactions } = props

  return (
    <AgGridWrapper
      className="datagrid-borderless"
      gridOptions={gridOptions}
      columnDefs={columnDefs}
      rowData={interactions}
      placeholder={noInteractionsPlaceholder}
    />
  )
}
